/**
 * What the backend said when it refused to publish (CT-5552).
 *
 * A rejected publish is `DSW022`, and the reason an author can act on is in
 * the report attached to the envelope — "Section home-overview is not
 * published" tells them what to change, "couldn't publish" does not.
 *
 * Two shapes, because the endpoint uses both: a *successful* publish reports
 * `validationStatus` + `validationIssues` at the top of the body, while a
 * *rejected* one answers with `{ validationReport: { status, issues } }`
 * (verified against dev 2026-09-24). `validateApiResponse` throws on the
 * rejection and leaves the whole envelope on `error.response.data`, so this
 * reads from there.
 *
 * Kept out of the hook so it can be tested in this app's node environment,
 * which has no DOM — see the note at the top of `vitest.config.js`.
 */

/**
 * @param {Error|null|undefined} error The throw from `publishDashboard`.
 * @returns {{ rejected: boolean, issues: Array<object> }}
 */
export function readPublishRejection(error) {
  const body = error?.response?.data?.body ?? null;
  const report = body?.validationReport ?? null;
  const status = report?.status ?? body?.validationStatus ?? null;
  const issues = report?.issues ?? body?.validationIssues ?? [];

  return {
    rejected: status === 'REJECTED',
    issues: Array.isArray(issues) ? issues : [],
  };
}

/**
 * The issues as lines an author can read.
 *
 * Each issue is tried in order of how much it says: the backend's own
 * sentence, then its detail, then the bare code — a code is poor, but it is
 * something to search for, and it beats dropping the issue silently.
 *
 * @param {Array<object>} issues
 * @returns {string[]}
 */
export function publishIssueLines(issues) {
  if (!Array.isArray(issues)) return [];
  return issues
    .map((issue) => issue?.message || issue?.detail || issue?.code)
    .filter(Boolean)
    .map(String);
}

export default readPublishRejection;
